import { useState, useEffect } from 'react';
import api from '../services/api';
import { authService } from '@/services/index.js';
import { XMarkIcon, SparklesIcon, GiftIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';

const PromotionalBanner = () => {
  const [banners, setBanners] = useState([]);
  const [dismissedBanners, setDismissedBanners] = useState([]);
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDismissedBanners();
  }, []);

  // Reload banners whenever the route changes (to respect target_pages filtering)
  useEffect(() => {
    loadBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const mapPathToPage = (pathname) => {
    if (!pathname || pathname === '/') return 'home';
    // Remove leading slash and take first segment only (e.g. '/modules/123' => 'modules')
    const segment = pathname.replace(/^\//, '').split('/')[0];
    if (!segment) return 'home';
    // Normalize common route names if needed
    const synonymMap = {
      'login': 'login',
      'register': 'register',
      'checkout': 'checkout',
      'dashboard': 'dashboard',
      'modules': 'modules',
      'labs': 'labs',
      'capstones': 'capstones'
    };
    return synonymMap[segment] || segment;
  };

  const loadBanners = async () => {
    setLoading(true);
    try {
      const pageKey = mapPathToPage(location.pathname);
      const response = await api.get('/banners', {
        params: { page: pageKey }
      });
      setBanners(response.data || []);

      // Ensure CSRF cookie is present before POST view tracking (defensive)
      try {
        await authService.getCsrfCookie();
      } catch (e) {
        // Skip if cannot get cookie; view recording may 419 otherwise
      }

      for (const banner of (response.data || [])) {
        recordView(banner.id);
      }
    } catch (error) {
      // Silent fail; optionally log for debugging
      // console.debug('Failed to load banners', error);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDismissedBanners = () => {
    const dismissed = JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
    setDismissedBanners(dismissed);
  };

  const recordView = async (bannerId) => {
    try {
      try { await authService.getCsrfCookie(); } catch (e) { /* ignore csrf retrieval error */ }
      await api.post(`/banners/${bannerId}/view`);
    } catch (error) {
      // Silent fail
    }
  };

  const dismissBanner = async (banner) => {
    try {
      try { await authService.getCsrfCookie(); } catch (e) { /* ignore csrf retrieval error */ }
      await api.post(`/banners/${banner.id}/dismiss`);
      const updated = [...dismissedBanners, banner.id];
      setDismissedBanners(updated);
      localStorage.setItem('dismissedBanners', JSON.stringify(updated));
    } catch (error) {
      // Silent fail
    }
  };

  const visibleBanners = banners.filter(b => !dismissedBanners.includes(b.id));

  const getTypeStyles = (type) => {
    const normalized = type === 'promo' ? 'promotional' : type; // handle legacy 'promo'
    const styles = {
      promotional: 'bg-gradient-to-r from-purple-600 to-pink-600',
      info: 'bg-blue-600',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      error: 'bg-red-600',
    };
    return styles[normalized] || styles.info;
  };

  if (loading) {
    return null; // Avoid flicker; could show skeleton if desired
  }

  if (visibleBanners.length === 0) return null;

  return (
    <>
      {visibleBanners.map((banner) => {
        const textColor = banner.text_color || '#ffffff';
        return (
          <div
            key={banner.id}
            className={`${banner.position === 'top' ? 'top-0' : 'bottom-0'} left-0 right-0 w-full relative z-40 ${getTypeStyles(banner.type)}`}
            style={{
              backgroundColor: banner.background_color || undefined,
              color: textColor,
            }}
          >
            <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between flex-wrap">
                <div className="flex-1 flex items-center">
                  {['promotional','promo'].includes(banner.type) && (
                    <SparklesIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  )}
                  {banner.coupon_code && (
                    <GiftIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                  )}
                  <div className="ml-3 font-medium">
                    <p className="text-lg font-bold">{banner.title}</p>
                    <p className="text-sm opacity-90 mt-1">{banner.message}</p>
                    {banner.coupon_code && (
                      <div className="mt-2 flex items-center space-x-2">
                        <span className="text-xs opacity-75">Code:</span>
                        <code className="bg-white bg-opacity-20 px-2 py-1 rounded text-sm font-mono font-bold">
                          {banner.coupon_code}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(banner.coupon_code);
                            alert('Coupon code copied!');
                          }}
                          className="text-xs underline hover:no-underline"
                        >
                          Copy
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-4 mt-3 sm:mt-0">
                  {banner.button_text && banner.button_link && (
                    <a
                      href={banner.button_link}
                      className="flex-shrink-0 px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                    >
                      {banner.button_text}
                    </a>
                  )}
                  {banner.dismissible && (
                    <button
                      onClick={() => dismissBanner(banner)}
                      className="flex-shrink-0 p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default PromotionalBanner;
